import React, { useState, useEffect } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { db } from '../firebase';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    // Fetch orders from both admin and customer paths
    const adminOrdersRef = ref(db, 'Watch/admin/orders');
    const unsubscribe = onValue(adminOrdersRef, (snapshot) => {
      const ordersData = snapshot.val();
      if (ordersData) {
        // Convert to array and add customer details from customer path
        const ordersArray = Object.entries(ordersData).map(([key, value]) => ({
          id: key,
          ...value,
        }));
        
        // Sort by date (newest first)
        ordersArray.sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
        setOrders(ordersArray);
      } else {
        setOrders([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
  if (window.confirm(`Change order status to ${newStatus}?`)) {
    try {
      // Find the order in our local state
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        throw new Error('Order not found');
      }

      // Update in admin orders
      await update(ref(db, `Watch/admin/orders/${orderId}`), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });

      // Also update in customer's orders under their mobile number
      if (order.customerMobile) {
        // First find the order in the customer's mobile path
        const customerOrdersRef = ref(db, `Watch/customers/${order.customerMobile}/orders`);
        const unsubscribe = onValue(customerOrdersRef, (snapshot) => {
          const customerOrders = snapshot.val();
          if (customerOrders) {
            Object.entries(customerOrders).forEach(([key, value]) => {
              if (value.orderId === order.orderId || key === orderId) {
                update(ref(db, `Watch/customers/${order.customerMobile}/orders/${key}`), {
                  status: newStatus,
                  updatedAt: new Date().toISOString(),
                });
              }
            });
          }
          // Unsubscribe after finding and updating the order
          unsubscribe();
        }, {
          onlyOnce: true // This ensures we don't keep listening after the first data load
        });
      }

      alert('Order status updated successfully');
      
      // Update local state to reflect the change immediately
      setOrders(prevOrders => 
        prevOrders.map(o => 
          o.id === orderId ? { ...o, status: newStatus, updatedAt: new Date().toISOString() } : o
        )
      );
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    }
  }
};

  const filteredOrders = orders
    .filter((order) => filterStatus === 'all' || order.status === filterStatus)
    .filter((order) => {
      if (!searchQuery) return true;
      const searchLower = searchQuery.toLowerCase();
      return (
        order.id.toLowerCase().includes(searchLower) ||
        (order.customerName && order.customerName.toLowerCase().includes(searchLower)) ||
        (order.customerEmail && order.customerEmail.toLowerCase().includes(searchLower)) ||
        (order.orderId && order.orderId.toLowerCase().includes(searchLower))
      );
    });

  const statusColors = {
    pending: '#ffa500',
    processing: '#1890ff',
    shipped: '#5470c6',
    delivered: '#52c41a',
    cancelled: '#f5222d',
    refunded: '#722ed1',
  };

  const getPaymentMethodDisplay = (payment) => {
    if (!payment) return 'N/A';
    if (payment.type === 'credit-card') return `Credit Card (•••• ${payment.cardLast4 || '••••'})`;
    if (payment.type === 'paypal') return 'PayPal';
    if (payment.type === 'cash-on-delivery') return 'Cash on Delivery';
    return payment.type || 'N/A';
  };

  const toggleExpandOrder = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '24px', fontSize: '28px' }}>Order Management</h1>
      
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <select 
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '10px 16px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            minWidth: '200px'
          }}
        >
          <option value="all">All Orders</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
          <option value="refunded">Refunded</option>
        </select>

        <input
          type="text"
          placeholder="Search by order ID, name, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '10px 16px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            flex: '1',
            maxWidth: '400px',
            minWidth: '250px'
          }}
        />
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', padding: '40px' }}>Loading orders...</p>
      ) : filteredOrders.length === 0 ? (
        <p style={{ textAlign: 'center', padding: '40px' }}>No orders found</p>
      ) : (
        <div style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
          {filteredOrders.map((order) => (
            <div key={order.id} style={{ borderBottom: '1px solid #eee', backgroundColor: expandedOrder === order.id ? '#fafafa' : '#fff' }}>
              <div 
                onClick={() => toggleExpandOrder(order.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ flex: '1 1 200px', fontWeight: '600' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Order #</div>
                  <div>{order.orderId || order.id}</div>
                </div>
                
                <div style={{ flex: '1 1 150px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Date</div>
                  <div>{new Date(order.date || order.createdAt).toLocaleDateString()}</div>
                </div>
                
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Customer</div>
                  <div>{order.customerName || `${order.shippingAddress?.firstName} ${order.shippingAddress?.lastName}` || 'Guest'}</div>
                </div>
                
                <div style={{ flex: '0 0 120px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Status</div>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: statusColors[order.status?.toLowerCase()] || '#666'
                  }}>
                    {order.status?.toUpperCase() || 'UNKNOWN'}
                  </span>
                </div>
                
                <div style={{ flex: '0 0 100px', textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Total</div>
                  <div style={{ fontWeight: '600' }}>₹{order.total?.toFixed(2) || '0.00'}</div>
                </div>
                
                <div style={{ flex: '0 0 24px', textAlign: 'center' }}>
                  {expandedOrder === order.id ? '▲' : '▼'}
                </div>
              </div>

              {expandedOrder === order.id && (
                <div style={{ padding: '20px', borderTop: '1px solid #eee', backgroundColor: '#fff' }}>
                  <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', marginBottom: '24px' }}>
                    <div style={{ flex: '1 1 300px' }}>
                      <h3 style={{ marginBottom: '16px', fontSize: '18px' }}>Customer Information</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '8px' }}>
                        <div style={{ color: '#666' }}>Name:</div>
                        <div>{order.customerName || `${order.shippingAddress?.firstName} ${order.shippingAddress?.lastName}` || 'N/A'}</div>
                        
                        <div style={{ color: '#666' }}>Email:</div>
                        <div>{order.customerEmail || order.shippingAddress?.email || 'N/A'}</div>
                        
                        <div style={{ color: '#666' }}>Phone:</div>
                        <div>{order.customerPhone || order.shippingAddress?.phone || 'N/A'}</div>
                        
                        <div style={{ color: '#666' }}>Address:</div>
                        <div>
                          {order.shippingAddress ? (
                            <>
                              {order.shippingAddress.address}<br />
                              {order.shippingAddress.city}, {order.shippingAddress.country}<br />
                              {order.shippingAddress.zipCode}
                            </>
                          ) : 'N/A'}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ flex: '1 1 300px' }}>
                      <h3 style={{ marginBottom: '16px', fontSize: '18px' }}>Order Summary</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '8px' }}>
                        <div style={{ color: '#666' }}>Payment Method:</div>
                        <div>{getPaymentMethodDisplay(order.paymentMethod)}</div>
                        
                        <div style={{ color: '#666' }}>Subtotal:</div>
                        <div>₹{order.subtotal?.toFixed(2) || '0.00'}</div>
                        
                        <div style={{ color: '#666' }}>Shipping:</div>
                        <div>₹{order.shippingCost?.toFixed(2) || '0.00'}</div>
                        
                        <div style={{ color: '#666' }}>Tax:</div>
                        <div>₹{order.tax?.toFixed(2) || '0.00'}</div>
                        
                        <div style={{ color: '#666', fontWeight: '600' }}>Total:</div>
                        <div style={{ fontWeight: '600' }}>₹{order.total?.toFixed(2) || '0.00'}</div>
                      </div>
                    </div>
                  </div>
                  
                  <h3 style={{ marginBottom: '16px', fontSize: '18px' }}>Order Items</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #eee' }}>Product</th>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #eee' }}>Price</th>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #eee' }}>Quantity</th>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #eee' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items && Object.values(order.items).map((item, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '12px', border: '1px solid #eee' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {item.image && (
                                  <img 
                                    src={item.image} 
                                    alt={item.name} 
                                    style={{ width: '50px', height: '50px', objectFit: 'contain' }} 
                                  />
                                )}
                                <div>
                                  <div style={{ fontWeight: '500' }}>{item.name}</div>
                                  {item.brand && <div style={{ fontSize: '14px', color: '#666' }}>{item.brand}</div>}
                                  {item.selectedColor && <div style={{ fontSize: '14px', color: '#666' }}>Color: {item.selectedColor}</div>}
                                  {item.selectedSize && <div style={{ fontSize: '14px', color: '#666' }}>Size: {item.selectedSize}</div>}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #eee' }}>
                              {item.discount ? (
                                <>
                                  <div style={{ textDecoration: 'line-through', color: '#999' }}>
                                    ₹{item.price.toFixed(2)}
                                  </div>
                                  <div>
                                    ₹{(item.price * (1 - (item.discount / 100))).toFixed(2)}
                                  </div>
                                  <div style={{ color: 'green', fontSize: '12px' }}>
                                    {item.discount}% off
                                  </div>
                                </>
                              ) : (
                                `₹${item.price.toFixed(2)}`
                              )}
                            </td>
                            <td style={{ padding: '12px', border: '1px solid #eee' }}>{item.quantity}</td>
                            <td style={{ padding: '12px', border: '1px solid #eee' }}>
                              ₹{(item.price * (1 - (item.discount || 0) / 100) * item.quantity).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      {order.updatedAt && (
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          Last updated: {new Date(order.updatedAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          minWidth: '200px'
                        }}
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;